/**
 * 페이지네이션에 표시할 페이지 번호들을 계산하는 함수
 * @param currentPage 현재 페이지 (1부터 시작)
 * @param totalPages 전체 페이지 수
 * @param siblingCount 현재 페이지 주변에 보여줄 페이지 수 (기본값: 1)
 * @returns 표시할 페이지 번호 배열 (ellipsis는 "..." 문자열로 표시)
 */
export function getPaginationRange(
  currentPage: number,
  totalPages: number,
  siblingCount: number = 1
): (number | string)[] {
  const totalPageNumbers = siblingCount + 5; // 1 + 2*siblingCount + 2 (처음/끝)

  if (totalPageNumbers >= totalPages) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 2;

  const firstPageIndex = 1;
  const lastPageIndex = totalPages;

  if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);

    return [...leftRange, '...', totalPages];
  }

  if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = Array.from(
      { length: rightItemCount },
      (_, i) => totalPages - rightItemCount + i + 1
    );

    return [firstPageIndex, '...', ...rightRange];
  }

  if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const middleRange = Array.from(
      { length: rightSiblingIndex - leftSiblingIndex + 1 },
      (_, i) => leftSiblingIndex + i
    );

    return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex];
  }

  return Array.from({ length: totalPages }, (_, i) => i + 1);
}

/**
 * 페이지 정보를 포맷팅하는 함수
 */
export function formatPaginationInfo(
  currentPage: number,
  pageSize: number,
  totalCount: number
): {
  from: number;
  to: number;
  total: number;
  description: string;
} {
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalCount);

  return {
    from,
    to,
    total: totalCount,
    description: `Showing ${from.toLocaleString()} to ${to.toLocaleString()} of ${totalCount.toLocaleString()} entries`,
  };
}

/**
 * 페이지 크기 옵션을 생성하는 함수
 */
export function getPageSizeOptions(
  currentPageSize: number,
  totalCount: number
): number[] {
  const defaultOptions = [5, 10, 20, 50, 100];

  const options = [...defaultOptions];
  if (!options.includes(currentPageSize)) {
    options.push(currentPageSize);
    options.sort((a, b) => a - b);
  }

  const filteredOptions = options.filter((size) => size <= totalCount);

  return filteredOptions.length > 0
    ? filteredOptions
    : [Math.min(10, totalCount)];
}
